import { useState } from 'react';
import { api, useApi } from '../api';
import { Button, Card, ErrorBanner, Loading, formatDateTime } from '../components/ui';
import { useT, type Clave } from '../i18n';
import { useAviso } from '../avisos';

type Usuario = {
  id: string;
  email: string;
  displayName: string;
  role: 'admin' | 'operator' | 'viewer';
  onboardedAt: string | null;
  createdAt: string;
};

/**
 * Una contraseña que nadie tiene que inventar.
 *
 * ── Por qué en grupos y con este alfabeto ─────────────────────────────────────
 * Porque se copia a mano más veces de las que uno cree: se pega en un chat, se lee en voz alta,
 * se escribe en otro dispositivo. Los guiones dan dónde apoyar la vista, y el alfabeto excluye
 * los caracteres que se confunden entre sí —`0/O`, `1/l/I`— que son los que producen un «no me
 * anda» que en realidad es una letra mal leída.
 *
 * Cuatro grupos de seis sobre 54 símbolos son ~138 bits: de sobra, y sigue siendo legible.
 *
 * `crypto.getRandomValues` y no `Math.random`: el segundo es predecible y esto es una llave.
 */
function generarPassword(): string {
  const alfabeto = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(24));

  const letras = Array.from(bytes, (byte) => alfabeto[byte % alfabeto.length]);
  return [0, 6, 12, 18].map((desde) => letras.slice(desde, desde + 6).join('')).join('-');
}

const ROLES: readonly (readonly [string, Clave, Clave])[] = [
  ['operator', 'rol.operator', 'rol.operatorQue'],
  ['admin', 'rol.admin', 'rol.adminQue'],
  ['viewer', 'rol.viewer', 'rol.viewerQue'],
] as const;

/**
 * Las cuentas del panel.
 *
 * ── Qué comparten y qué no ────────────────────────────────────────────────────
 * Comparten el despliegue y nada más. Cada cuenta ve **sólo sus influencers** y usa **sólo sus
 * credenciales**: su cuenta de Cloudflare, su clave de fal, su saldo. Por eso una cuenta nueva
 * abre el panel vacío, y por eso lo que gaste una no lo paga otra.
 *
 * El aislamiento no se hace acá sino en el servidor, en el repositorio: esta pantalla sólo crea
 * las filas. Si dependiera de la interfaz, bastaría con escribir una URL a mano para saltearlo.
 */
export function Usuarios() {
  const { t } = useT();
  const aviso = useAviso();
  const state = useApi<{ users: Usuario[] }>('/api/users');
  const [abierto, setAbierto] = useState(false);
  const [email, setEmail] = useState('');
  const [nombre, setNombre] = useState('');
  const [password, setPassword] = useState('');
  const [rol, setRol] = useState<Usuario['role']>('operator');
  const [guardando, setGuardando] = useState(false);

  if (state.loading && !state.data) return <Loading label={t('cuentas.cargando')} />;
  if (state.error) return <ErrorBanner error={state.error} onRetry={state.reload} />;

  const usuarios = state.data?.users ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">{t('cuentas.titulo')}</h1>
          <p className="mt-1 text-sm text-[var(--color-ink-muted)]">{t('cuentas.bajada')}</p>
        </div>
        {!abierto && <Button onClick={() => setAbierto(true)}>{t('cuentas.crear')}</Button>}
      </div>

      {abierto && (
        <Card title={t('cuentas.nueva')}>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs">
              <span className="mb-1 block text-[var(--color-ink-muted)]">{t('cuentas.nombre')}</span>
              <input
                value={nombre}
                onChange={(event) => setNombre(event.target.value)}
                placeholder={t('cuentas.nombreEjemplo')}
                className="w-full rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 text-sm"
              />
            </label>

            <label className="text-xs">
              <span className="mb-1 block text-[var(--color-ink-muted)]">Correo</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="persona@correo.com"
                className="w-full rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 text-sm"
              />
            </label>

            <label className="text-xs">
              <span className="mb-1 block text-[var(--color-ink-muted)]">
                {t('cuentas.password')}
              </span>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={t('cuentas.passwordEjemplo')}
                  className="min-w-0 flex-1 rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => setPassword(generarPassword())}
                  title={t('cuentas.generarTitulo')}
                  className="shrink-0 rounded-xl border border-[var(--color-accent)] px-3 py-2 text-xs font-medium text-[var(--color-accent)] transition hover:opacity-80"
                >
                  {t('cuentas.generar')}
                </button>
                {password && (
                  <button
                    type="button"
                    onClick={() => void navigator.clipboard.writeText(password)}
                    title={t('cuentas.copiarTitulo')}
                    className="shrink-0 rounded-xl border border-[var(--color-border-subtle)] px-3 py-2 text-xs transition hover:border-[var(--color-border-strong)]"
                  >
                    {t('comun.copiar')}
                  </button>
                )}
              </div>
            </label>

            <label className="text-xs">
              <span className="mb-1 block text-[var(--color-ink-muted)]">Rol</span>
              <select
                value={rol}
                onChange={(event) => setRol(event.target.value as Usuario['role'])}
                className="w-full rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 text-sm"
              >
                {ROLES.map(([valor, clave]) => (
                  <option key={valor} value={valor}>
                    {t(clave)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <p className="mt-2 text-xs text-[var(--color-ink-muted)]">
            {ROLES.find(([valor]) => valor === rol)?.[2]}
          </p>

          {/*
            La contraseña se muestra en claro y en monoespaciada a propósito: hay que
            copiarla y pasársela a la persona. Ocultarla con puntitos obligaría a escribirla
            dos veces sin poder verificarla, que es como se generan las que nadie puede usar.
          */}
          <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
            {t('cuentas.seGuardaCifrada')}
          </p>

          <div className="mt-4 flex gap-2">
            <Button
              disabled={guardando || password.length < 12 || !email.includes('@')}
              onClick={() => {
                setGuardando(true);
                void api
                  .post('/api/users', {
                    email: email.trim(),
                    displayName: nombre.trim() || email.trim(),
                    password,
                    role: rol,
                  })
                  .then(() => {
                    setAbierto(false);
                    setEmail('');
                    setNombre('');
                    setPassword('');
                    return state.reload();
                  })
                  .finally(() => setGuardando(false));
              }}
            >
              {t(guardando ? 'cuentas.creando' : 'cuentas.crearBoton')}
            </Button>
            <Button variant="secondary" onClick={() => setAbierto(false)}>
              {t('comun.cancelar')}
            </Button>
          </div>
        </Card>
      )}

      <Card title={`Cuentas · ${usuarios.length}`}>
        <ul className="divide-y divide-[var(--color-border-subtle)]">
          {usuarios.map((usuario) => (
            <li key={usuario.id} className="flex flex-wrap items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">
                  {usuario.displayName}
                  <span className="ml-2 rounded-full bg-[var(--color-surface-sunken)] px-2 py-0.5 text-[0.65rem] font-medium text-[var(--color-ink-muted)]">
                    {(() => {
                      const rol = ROLES.find(([valor]) => valor === usuario.role);
                      return rol ? t(rol[1]) : usuario.role;
                    })()}
                  </span>
                </p>
                <p className="mt-0.5 text-xs text-[var(--color-ink-muted)]">
                  {t('cuentas.desde', {
                    correo: usuario.email,
                    cuando: formatDateTime(usuario.createdAt),
                  })}
                  {usuario.onboardedAt === null && t('cuentas.sinGuia')}
                </p>
              </div>

              {/*
                Cambiar la contraseña, y no borrar y recrear.

                No hay recuperación por correo —el correo es sólo el identificador—, así que sin
                esto una contraseña olvidada se «arregla» borrando la cuenta y creándola de
                nuevo. Y ahí está la trampa: la cuenta nueva tiene otro identificador, y sus
                influencers son de la vieja. Se volverían invisibles sin que nada falle.
              */}
              <button
                type="button"
                onClick={() => {
                  const nueva = generarPassword();
                  if (
                    !window.confirm(
                      t('cuentas.confirmarPassword', {
                        quien: usuario.displayName,
                        clave: nueva,
                      }),
                    )
                  ) {
                    return;
                  }

                  void api
                    .post(`/api/users/${usuario.id}/password`, { password: nueva })
                    .then(() => void navigator.clipboard.writeText(nueva))
                    // El fallo lo avisa el cliente de la API; acá sólo el éxito.
                    .then(() => aviso.ok(t('cuentas.passwordCambiada')))
                    .catch(() => undefined);
                }}
                className="text-xs text-[var(--color-ink-muted)] underline underline-offset-2 hover:text-[var(--color-accent)]"
              >
                {t('cuentas.nuevaPassword')}
              </button>

              <button
                type="button"
                onClick={() => {
                  if (!window.confirm(t('cuentas.confirmarQuitar', { quien: usuario.displayName })))
                    return;
                  void api.del(`/api/users/${usuario.id}`).then(state.reload);
                }}
                className="text-xs text-[var(--color-ink-muted)] underline underline-offset-2 hover:text-[var(--color-danger)]"
              >
                {t('cuentas.quitar')}
              </button>
            </li>
          ))}
        </ul>

        <p className="mt-4 max-w-2xl text-xs text-[var(--color-ink-muted)]">
          {t('cuentas.correoEsIdAntes')}
          <strong>{t('cuentas.sinRecuperacion')}</strong>
          {t('cuentas.correoEsIdDespues')}
          <br />
          <br />
          {t('cuentas.quitarNoBorraAntes')}
          <strong>{t('cuentas.noLasRecupera')}</strong>
          {t('cuentas.quitarNoBorraDespues')}
        </p>
      </Card>
    </div>
  );
}
